import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Scale } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'

// Delivery Balance — what we collect from customers in delivery fees vs what
// we owe ambassadors in earnings (base + per-km), per ambassador, with a
// per-order drill-down. Today's use: compute the monthly top-up for the one
// salaried ambassador (his salary covers the car allowance, so he isn't paid
// the fee; `earnings` is the extra we pay on top). Tips are shown but never
// netted in — they're paid same-day from the till.

interface AmbassadorRow {
  driver_id: string | null
  driver_name: string
  deliveries: number
  fees_collected_fils: number
  earnings_fils: number
  tips_fils: number
  net_fils: number
}
interface BalanceResponse {
  from: string
  to: string
  ambassadors: AmbassadorRow[]
  totals: {
    deliveries: number
    fees_collected_fils: number
    earnings_fils: number
    tips_fils: number
    net_fils: number
  }
}
interface OrderRow {
  id: string
  order_number: string
  delivery_fee_fils: number
  earnings_fils: number
  tip_fils: number
  delivered_at: string
}

// First + last day of the current month (local), as YYYY-MM-DD.
function defaultRange(): { from: string; to: string } {
  const now = new Date()
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const to = now.toISOString().slice(0, 10)
  return { from, to }
}

function SummaryCard({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-xl font-semibold mt-1 ${tone ?? 'text-white'}`}>{value}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
  )
}

function OrderDrill({ driverId, from, to }: { driverId: string | null; from: string; to: string }) {
  const key = driverId ?? 'unassigned'
  const { data, isLoading } = useQuery<{ orders: OrderRow[] }>({
    queryKey: ['delivery-balance-orders', key, from, to],
    queryFn: () => api.get(`/delivery-balance/${key}/orders?from=${from}&to=${to}`).then(r => r.data),
  })
  if (isLoading) return <tr><td colSpan={6} className="px-6 py-3 text-xs text-gray-500">Loading orders…</td></tr>
  const orders = data?.orders ?? []
  if (orders.length === 0) return <tr><td colSpan={6} className="px-6 py-3 text-xs text-gray-500">No delivered orders in range.</td></tr>
  return (
    <>
      {orders.map(o => (
        <tr key={o.id} className="bg-[#0f0f0f] text-xs">
          <td className="px-6 py-2 text-gray-400">{o.order_number}</td>
          <td className="px-4 py-2 text-gray-500">{formatDateTime(o.delivered_at)}</td>
          <td className="px-4 py-2 text-right text-gray-300 tabular-nums">{formatCurrency(o.delivery_fee_fils)}</td>
          <td className="px-4 py-2 text-right text-gray-300 tabular-nums">{formatCurrency(o.earnings_fils)}</td>
          <td className="px-4 py-2 text-right text-gray-500 tabular-nums">{o.tip_fils > 0 ? formatCurrency(o.tip_fils) : '—'}</td>
          <td className={`px-4 py-2 text-right tabular-nums ${o.delivery_fee_fils - o.earnings_fils >= 0 ? 'text-gray-300' : 'text-amber-400'}`}>
            {formatCurrency(o.delivery_fee_fils - o.earnings_fils)}
          </td>
        </tr>
      ))}
    </>
  )
}

export default function DeliveryBalance() {
  const [range, setRange] = useState(defaultRange)
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery<BalanceResponse>({
    queryKey: ['delivery-balance', range.from, range.to],
    queryFn: () => api.get(`/delivery-balance?from=${range.from}&to=${range.to}`).then(r => r.data),
  })

  const t = data?.totals

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-2 mb-1">
        <Scale size={20} className="text-[#C41E3A]" />
        <h1 className="text-xl font-semibold text-white">Delivery Balance</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Delivery fees collected from customers vs ambassador earnings owed (base fare + per-km), per ambassador.
        Earnings is the monthly top-up to pay each ambassador. Tips are paid same-day from the till and are not netted in.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <label className="text-xs text-gray-400">
          From
          <input
            type="date"
            value={range.from}
            onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
            className="block mt-1 bg-[#1a1a1a] border border-[#2e2e2e] rounded-md px-2 py-1.5 text-sm text-white"
          />
        </label>
        <label className="text-xs text-gray-400">
          To
          <input
            type="date"
            value={range.to}
            onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
            className="block mt-1 bg-[#1a1a1a] border border-[#2e2e2e] rounded-md px-2 py-1.5 text-sm text-white"
          />
        </label>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && <p className="text-sm text-red-400">Failed to load delivery balance.</p>}

      {data && t && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <SummaryCard label="Fees collected" value={formatCurrency(t.fees_collected_fils)} hint={`${t.deliveries} deliveries`} />
            <SummaryCard label="Ambassador earnings" value={formatCurrency(t.earnings_fils)} hint="base + per-km (monthly top-up)" />
            <SummaryCard
              label="Net on delivery"
              value={`${t.net_fils >= 0 ? '+' : ''}${formatCurrency(t.net_fils)}`}
              hint={t.net_fils >= 0 ? 'surplus' : 'deficit'}
              tone={t.net_fils >= 0 ? 'text-green-400' : 'text-amber-400'}
            />
            <SummaryCard label="Tips (paid same-day)" value={formatCurrency(t.tips_fils)} hint="not part of the balance" />
          </div>

          <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-[#2e2e2e]">
                  <th className="px-4 py-3 font-medium">Ambassador</th>
                  <th className="px-4 py-3 font-medium text-right">Deliveries</th>
                  <th className="px-4 py-3 font-medium text-right">Fees collected</th>
                  <th className="px-4 py-3 font-medium text-right">Earnings owed</th>
                  <th className="px-4 py-3 font-medium text-right">Tips</th>
                  <th className="px-4 py-3 font-medium text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.ambassadors.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">No deliveries in this range.</td></tr>
                )}
                {data.ambassadors.map(a => {
                  const key = a.driver_id ?? 'unassigned'
                  const isOpen = expanded === key
                  return (
                    <>
                      <tr
                        key={key}
                        className="border-b border-[#2e2e2e] hover:bg-white/5 cursor-pointer"
                        onClick={() => setExpanded(isOpen ? null : key)}
                      >
                        <td className="px-4 py-3 text-white flex items-center gap-1.5">
                          {isOpen ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                          {a.driver_name}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-300 tabular-nums">{a.deliveries}</td>
                        <td className="px-4 py-3 text-right text-gray-300 tabular-nums">{formatCurrency(a.fees_collected_fils)}</td>
                        <td className="px-4 py-3 text-right text-gray-300 tabular-nums">{formatCurrency(a.earnings_fils)}</td>
                        <td className="px-4 py-3 text-right text-gray-500 tabular-nums">{a.tips_fils > 0 ? formatCurrency(a.tips_fils) : '—'}</td>
                        <td className={`px-4 py-3 text-right font-medium tabular-nums ${a.net_fils >= 0 ? 'text-green-400' : 'text-amber-400'}`}>
                          {a.net_fils >= 0 ? '+' : ''}{formatCurrency(a.net_fils)}
                        </td>
                      </tr>
                      {isOpen && (
                        <>
                          <tr className="text-[10px] uppercase tracking-wider text-gray-600 bg-[#0f0f0f]">
                            <td className="px-6 py-2">Order</td>
                            <td className="px-4 py-2">Delivered</td>
                            <td className="px-4 py-2 text-right">Fee</td>
                            <td className="px-4 py-2 text-right">Earnings</td>
                            <td className="px-4 py-2 text-right">Tip</td>
                            <td className="px-4 py-2 text-right">Net</td>
                          </tr>
                          <OrderDrill driverId={a.driver_id} from={data.from} to={data.to} />
                        </>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
